import { PlanModeCamera } from "../plan/PlanModeCamera";
import { computeAnalyticPlacement, readVpInfo } from "../plan/planModeUtils";
import { DtModel } from "../DtModelLMV";
import { makeTemporaryModelUrn } from "../encoding/base64";
import * as dte from "../DtEventTypes";
import { AllFacetsScopes, FacetTypes } from "../facets/Facets";
import { av, THREE } from "@adsk/lmv";

export class FloorPlanManager {
	#boundOnFacetsChanged;
	#boundCheckFloorplans;
	#active = false;
	#selectedLevelDbId = null;
	#loadedPdfModel = null;
	#revertRoomIsolation = null;
	/** undefined = not yet determined; true/false once known */
	#hasFloorplans;

	constructor(facility) {
		this.facility = facility;
		this.planModeCamera = new PlanModeCamera(facility);
		this.#boundOnFacetsChanged = this.#onFacetsChanged.bind(this);
		this.#boundCheckFloorplans = this.#checkFloorplans.bind(this);
	}

	init() {
		const { eventTarget, viewer } = this.facility;

		eventTarget.addEventListener(dte.DT_FACETS_LOADED, this.#boundOnFacetsChanged);
		eventTarget.addEventListener(dte.DT_FACETS_UPDATED, this.#boundOnFacetsChanged);
		viewer?.addEventListener(av.OBJECT_TREE_CREATED_EVENT, this.#boundCheckFloorplans);
	}

	dispose() {
		const { eventTarget, viewer } = this.facility;

		this.exitPlanMode();
		this.#reset();

		eventTarget.removeEventListener(dte.DT_FACETS_LOADED, this.#boundOnFacetsChanged);
		eventTarget.removeEventListener(dte.DT_FACETS_UPDATED, this.#boundOnFacetsChanged);
		viewer?.removeEventListener(av.OBJECT_TREE_CREATED_EVENT, this.#boundCheckFloorplans);

		this.#setHasFloorplans(undefined);
	}

	isPlanModeActive() {
		return this.#active;
	}

	enterPlanMode() {
		if (!this.facility.app.flags?.noBim || this.#active) {
			return Promise.resolve();
		}

		const cameraEnterPromise = this.planModeCamera.enter();
		this.#active = true;
		this.#dispatchPlanModeChanged();
		this.#resolveSelectedLevelFromFacets();
		return cameraEnterPromise;
	}

	exitPlanMode(restoreCamera = false) {
		if (!this.#active) {
			return;
		}
		this.#active = false;
		this.planModeCamera.exit(restoreCamera);
		this.clearLevelSelection();
		this.#dispatchPlanModeChanged();
	}

	#reset() {
		this.planModeCamera.resetForViewerChange(this.facility.viewer);
		this.#active = false;
		this.#selectedLevelDbId = null;
		this.#loadedPdfModel = null;
		this.#revertRoomIsolation = null;
	}

	getPlanModeCamera() {
		return this.planModeCamera;
	}

	/** @returns {boolean|undefined} undefined until determined */
	hasFloorplans() {
		return this.#hasFloorplans;
	}

	getLoadedPdfModel() {
		return this.#loadedPdfModel;
	}

	/**
	 * Selects a level for 2D display: isolates its rooms,
	 * flies the plan-mode camera to its bounds if active, and
	 * loads/unloads its PDF floorplan overlay.
	 */
	async selectLevel(dbId) {
		if (dbId == null) {
			this.clearLevelSelection();
			return;
		}
		if (dbId === this.#selectedLevelDbId) {
			return;
		}

		this.#beginLevelViewTransition(dbId);
		const isStale = () => this.#selectedLevelDbId !== dbId;

		this.#unloadCurrentPdf();

		const level = this.facility.getDefaultModel()?.getLocationLevels()?.[dbId];
		if (!level) {
			this.exitPlanMode();
			return;
		}

		const isolated = await this.#applyLevelRoomIsolation(level, isStale);
		if (!isolated || isStale()) {
			isolated?.revert();
			return;
		}
		this.#revertRoomIsolation = isolated.revert;

		this.facility.viewer.setAggregateSelection([]);
		const levelBBox = isolated.worldBBox;
		if (levelBBox) {
			this.planModeCamera.flyToBounds(levelBBox);
		}

		const pdfDerivativeUrn = level.settings?.pdfDerivativeUrn;
		if (!pdfDerivativeUrn) {
			return;
		}

		const model = await this.#loadLevelPdf(pdfDerivativeUrn, levelBBox, level.settings?.originOffset);
		if (isStale()) {
			this.#unloadModel(model);
			return;
		}

		this.#loadedPdfModel = model;
	}

	clearLevelSelection() {
		this.#beginLevelViewTransition(null);
		this.facility.facetsManager?.clearFilter(FacetTypes.locations, false, AllFacetsScopes);
		this.#unloadCurrentPdf();
	}

	/** Shared bookkeeping at the start of any level-view transition (select or clear). */
	#beginLevelViewTransition(dbId) {
		this.#revertRoomIsolation?.();
		this.#revertRoomIsolation = null;
		this.#selectedLevelDbId = dbId;
	}

	/**
	 * Resolves which level should be selected from the current "locations" facet state.
	 * When multiple levels are selected, picks the last one. When none are selected,
	 * auto-selects the first level if one exists.
	 */
	#resolveSelectedLevelFromFacets() {
		if (!this.isPlanModeActive() || !this.facility.facetsManager?.isLoaded) {
			return;
		}

		const locationNodes = this.facility.facetsManager.getFacet(FacetTypes.locations) ?? [];
		const nodes = locationNodes.filter((node) => !node.isRoom && !node.isUnassignedId);
		const selectedNodes = nodes.filter((node) => node.selected);

		let targetNode;
		// If multiple levels are selected, select the last one
		if (selectedNodes.length > 1) {
			targetNode = selectedNodes[selectedNodes.length - 1];
			this.#selectLevelFacetByName(targetNode.id);
		} else if (selectedNodes.length === 0) {
			targetNode = nodes[0];
			if (!targetNode) {
				// No levels at all to show in plan mode - nothing meaningful left to display.
				this.exitPlanMode();
				return;
			}
			this.#selectLevelFacetByName(targetNode.id);
		} else {
			targetNode = selectedNodes[0];
		}

		if (targetNode.dbId !== this.#selectedLevelDbId) {
			this.selectLevel(targetNode.dbId).catch((err) => {
				console.error('Failed to select 2D level', err);
			});
		}
	}

	/** Selects a single level in the "locations" facet filter (change HUD facet selection). */
	#selectLevelFacetByName(levelName) {
		const facetsManager = this.facility.facetsManager;
		facetsManager.clearFilter(FacetTypes.locations, true, AllFacetsScopes);
		facetsManager.setVisibilityById(FacetTypes.locations, levelName, true, false, AllFacetsScopes);
	}

	/**
	 * Selects the given level's rooms in the "locations" facet filter and dims them, 
	 * returning the level's world bounding box and a cleanup that reverts the cosmetic dimming.
	 */
	async #applyLevelRoomIsolation(level, isStale) {
		const defaultModel = this.facility.getDefaultModel();
		const facetsManager = this.facility.facetsManager;
		const locationRooms = Object.values(defaultModel.getLocationRooms() ?? {});
		const ids = locationRooms.filter((r) => r.parentDbId === level.dbId).map((r) => r.dbId);

		await defaultModel.loadElements(ids);
		if (isStale?.()) {
			return null;
		}

		await this.facility.viewer.impl.waitForLoaderIdle();

		defaultModel.visibilityManager.isolate(ids);

		await this.facility.viewer.impl.waitForLoaderIdle();

		const roomOpacity = 0.3;
		const prevRoomOpacity = facetsManager.facetsEffects.getDefaultRoomOpacity();

		// Set our desired room opacity as the default, to keep other operations like location
		// highlighting from resetting rooms to fully opaque.
		facetsManager.facetsEffects.setDefaultRoomOpacity(roomOpacity);
		facetsManager.facetsEffects.makeRoomsOpaque(true, roomOpacity);
		const revert = () => {
			if (facetsManager.facetsEffects.getDefaultRoomOpacity() === roomOpacity) {
				facetsManager.facetsEffects.setDefaultRoomOpacity(prevRoomOpacity);
			}
			facetsManager.facetsEffects.makeRoomsOpaque(false);
			facetsManager.highlightLocations(true);
		};

		// Collect all room bboxes and union them into a single bbox to represent the level.
		const box = new THREE.Box3();
		const unioned = new THREE.Box3();
		for (const id of ids) {
			defaultModel.getElementBounds(id, box);
			unioned.union(box);
		}
		return {
			worldBBox: unioned.isEmpty() ? null : unioned,
			revert,
		};
	}

	#unloadCurrentPdf() {
		this.#unloadModel(this.#loadedPdfModel);
		this.#loadedPdfModel = null;
	}

	#unloadModel(model) {
		if (model) {
			this.facility.viewer.impl.unloadModel(model, false);
		}
	}

	#onFacetsChanged() {
		this.#checkFloorplans();
		this.#resolveSelectedLevelFromFacets();
	}

	#checkFloorplans() {
		const defaultModel = this.facility.getDefaultModel();
		const locations = defaultModel?.getLocationsMap();
		if (!locations) {
			return;
		}

		this.#setHasFloorplans(Boolean(defaultModel.getLocationLevelsWithFloorplans().length));
	}

	#setHasFloorplans(hasFloorplans) {
		if (hasFloorplans === this.#hasFloorplans) {
			return;
		}

		this.#hasFloorplans = hasFloorplans;
		this.facility.eventTarget.dispatchEvent({
			type: dte.DT_FLOORPLANS_CHANGED_EVENT,
			facility: this.facility,
			hasFloorplans,
		});
	}

	#dispatchPlanModeChanged() {
		this.facility.eventTarget.dispatchEvent({
			type: dte.DT_PLAN_MODE_CHANGED_EVENT,
			facility: this.facility,
			active: this.#active,
		});
	}

	async #loadLevelPdf(urn, levelBBox, originOffsetMm) {
		const cad2lbsPromise = import('@adsk/cad2lbs');

		const options = {
			keepCurrentModels: true,
			skipPropertyDb: true,
			page: 1,
			// hideBackground drops the full-page white sheet (dbId -1) so only PDF linework shows.
			hideBackground: true,
			modelObj: new DtModel(this.facility, makeTemporaryModelUrn()),
		};

		const model = await this.facility.viewer.loadPdfFromUrn(urn, options);

		// While we create a DtModel instance, it's not really a Tandem model, i.e. it doesn't have all of the data
		// that may be expected by various workflows. For example, it doesn't have an instance tree, and generally no
		// 'dbId2...' maps, so Tandem-specific uses of model.getData()... don't work. Similarly, all property worker
		// operations don't work either, because there is no PDB for the model, and the worker doesn't know about it in
		// general. To avoid running into issues because of that, we disable selection for this model, which is one of
		// the main entry points for potential issues. Note that this only prevents 'manual' selection.
		// Programmatic selection, i.e. calling viewer.set(Aggregate)Selection, is not prevented.
		model.ignoreRayIntersect = true;

		const pdfDoc = model.loader.pdf;

		const { pageToModelMm } = await cad2lbsPromise;

		const vpInfo = await readVpInfo(pdfDoc, pageToModelMm);

		const defaultModel = this.facility.getDefaultModel();
		const globalOffset = defaultModel.getGlobalOffset();
		const modelUnit = defaultModel.getUnitString();

		const placement =
			vpInfo && computeAnalyticPlacement(model, vpInfo, levelBBox, originOffsetMm, globalOffset, modelUnit);
		if (placement) {
			this.facility.viewer.impl.setPlacementTransform(model, placement);
			this.facility.viewer.impl.invalidate(true);
		} else {
			console.warn('loadLevelPdf: could not place this PDF (no VP viewport matrix, or a degenerate placement)');
		}

		return model;
	}
}
